import { FC, useEffect, useRef, useState } from 'react';
import { BKJIcon } from '@bookjane2/bookjane-design-library';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { useClickOutside } from 'hooks';
import { useTranslation } from 'hooks/useTranslation';
import { mergeRefs } from 'utils/mergeRefs';
import { getJ360ElementNode } from 'utils/getJ360ElementNode';
import { DROPDOWN_ROOT_ID } from 'constants/elements';
import {
  BKJCalendar,
  BKJDatePickerContext,
  IBKJDatePickerChangeEvent,
  IPayPeriodConfig,
} from 'components/BKJDatePicker';
import { isISO8601Tuple, ISO8601Type, ISO8601TupleType } from 'types/common.types';
import { formatPayPeriodRangeLabel } from 'pages/ReportsPage/components/AnalyticsDashboards/payPeriodFormatters';
import {
  DateRangeTrigger,
  PayPeriodCalendarWrapper,
} from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/FilterBar.styled';

interface PayPeriodDateInputProps {
  name: string;
  value: ISO8601TupleType;
  onChange: (range: ISO8601TupleType) => void;
  payPeriod?: IPayPeriodConfig;
  ariaLabel?: string;
  disabledTo?: ISO8601Type;
  calendarOffset?: [number, number];
}

export const PayPeriodDateInput: FC<PayPeriodDateInputProps> = ({
  name,
  value,
  onChange,
  payPeriod,
  ariaLabel,
  disabledTo,
  calendarOffset = [0, 6],
}) => {
  const { i18n } = useTranslation();
  const popupRef = useRef<HTMLElement | null>(null);
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(
    getJ360ElementNode(DROPDOWN_ROOT_ID),
  );
  const [isOpen, setIsOpen] = useState(false);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [{ name: 'offset', options: { offset: calendarOffset } }],
    placement: 'bottom-end',
  });

  useEffect(() => {
    if (!targetElement) {
      const element = document.createElement('div');
      element.setAttribute('id', DROPDOWN_ROOT_ID);
      document.body.appendChild(element);
      setTargetElement(element);
    }
  }, [targetElement]);

  useClickOutside([popupRef.current, targetElement, referenceElement], () => setIsOpen(false));

  const handleChange = (event: IBKJDatePickerChangeEvent): void => {
    const nextValue = event.target.value;
    if (isISO8601Tuple(nextValue)) onChange(nextValue);
  };

  return (
    <BKJDatePickerContext.Provider
      value={{
        type: 'PayPeriodRange',
        variant: 'SSFTopBar',
        name,
        value,
        onChange: handleChange,
        disabledTo,
        payPeriod,
      }}
    >
      <DateRangeTrigger
        type="button"
        $isOpen={isOpen}
        aria-label={ariaLabel}
        ref={setReferenceElement}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <BKJIcon iconName="ShiftsDate" fontSize={14} color="TextDarkGrey" />
        {formatPayPeriodRangeLabel(value, i18n.language)}
        <BKJIcon iconName="ArrowDown" fontSize={14} color="TextDarkGrey" />
      </DateRangeTrigger>
      {isOpen &&
        !!targetElement &&
        createPortal(
          <PayPeriodCalendarWrapper
            variant="SSFTopBar"
            ref={mergeRefs([popupRef, setPopperElement])}
            style={styles.popper}
            {...attributes.popper}
          >
            <BKJCalendar context={BKJDatePickerContext} setIsCalendarOpen={setIsOpen} />
          </PayPeriodCalendarWrapper>,
          targetElement,
        )}
    </BKJDatePickerContext.Provider>
  );
};
